/**
 * Pure adapter for the btgpt "boards" long-term-memory tool_result → the data a
 * <BoardTasksMessageBlock> renders. Kept dependency-free (no React/MUI) so it can
 * be unit-tested in isolation and imported cheaply.
 *
 * Contract (backend, see agent-memory-boards-design.md §8-9): a saved boards
 * tool result (a transcript turn's `toolResults` row, `{ tool: 'boards', action,
 * data }`, or the documented `{ …, result }`) — never a live chunk — where it
 * includes `board: { board_id, name, domain, web_url }` — web_url ends with
 * `/tasks/?boardID=<RAW mongo board id>` — plus items (`items` for list_items,
 * `created`/`updated` for bulk_upsert). Each item is
 * `{ item_id, text, status, priority, due_date?, web_url, updated_at }` where the
 * item web_url ends with `&taskID=<RAW mongo task id>`. The RAW ids in the query
 * params are the deliberate bridge into the web store (public ids are opaque).
 */

/** A single pill value carried on the SSE result: the same `{ label, bgcolor }`
 *  shape an OptionPickerCell renders. Used as the `mockValue` fallback before the
 *  board's real columns/options hydrate. */
export interface BoardCellOption {
  label?: string | null;
  bgcolor?: string | null;
}

/** One board item (task) row, adapted from the public boards tool_result. */
export interface BoardTaskItem {
  /** RAW mongo task id parsed from the item web_url `taskID` query param. */
  rawTaskId: string;
  text: string;
  status?: BoardCellOption | null;
  priority?: BoardCellOption | null;
  dueDate?: string;
  itemWebUrl?: string;
}

/** The data a BoardTasksMessageBlock renders. */
export interface BoardTasksBlockData {
  board: {
    name: string;
    webUrl: string;
    /** RAW mongo board id parsed from the board web_url `boardID` query param. */
    rawBoardId: string;
  };
  items: BoardTaskItem[];
  /** Which boards action produced this block. */
  action: string;
}

/** Boards actions that render a task block. */
const RENDERABLE_ACTIONS = new Set(['list_items', 'bulk_upsert', 'create_board']);

/** Pull a query-param value out of a (possibly relative) URL string. */
export function parseUrlParam(url: string | undefined, key: string): string | undefined {
  if (!url) return undefined;
  const m = url.match(new RegExp(`[?&]${key}=([^&#]+)`));
  return m ? decodeURIComponent(m[1]) : undefined;
}

/** Normalize a raw tool_result that may be wrapped in a `{ data }` / `{ result }`
 *  envelope down to the public JSON payload. A `data` that is an ARRAY is the
 *  v1 pager's item list (`{ data: [...], total, board }`), not an envelope —
 *  never unwrap through it. */
function unwrapResult(raw: any): any {
  if (!raw || typeof raw !== 'object') return raw;
  if (raw.board || raw.items || raw.created || raw.updated || Array.isArray(raw.data)) return raw;
  if (raw.result) return unwrapResult(raw.result);
  if (raw.data) return unwrapResult(raw.data);
  return raw;
}

/** True when a payload IS a public board shape (create_board / get_board return
 *  the board at top level rather than under a `board` key). */
function looksLikeBoard(v: any): boolean {
  return !!v && typeof v === 'object' && typeof v.web_url === 'string' && typeof v.board_id === 'string';
}

/** Adapt one item row from the public tool_result into a {@link BoardTaskItem}. */
function adaptItem(raw: any): BoardTaskItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const rawTaskId = parseUrlParam(raw.web_url, 'taskID');
  if (!rawTaskId) return null;
  return {
    rawTaskId,
    text: typeof raw.text === 'string' ? raw.text : '',
    status: raw.status ?? null,
    priority: raw.priority ?? null,
    dueDate: raw.due_date ?? undefined,
    itemWebUrl: raw.web_url,
  };
}

/**
 * Adapt a saved boards-tool result (a transcript turn's `toolResults` row) into
 * the data a BoardTasksMessageBlock renders, or `null` when it isn't a renderable
 * boards result. Accepts the persisted shape (`{ tool, action, data }`) and the
 * documented contract shape (`{ tool, action, result }`). The live stream never
 * carries tool results (v2), so there is no live shape.
 */
export function adaptBoardTasksResult(chunk: any): BoardTasksBlockData | null {
  if (!chunk || typeof chunk !== 'object') return null;

  const tool = chunk.tool;
  if (tool !== 'boards') return null;

  const action = chunk.action;
  if (!action || !RENDERABLE_ACTIONS.has(action)) return null;

  const result = unwrapResult(chunk.result ?? chunk.data);
  // Board ref: `result.board` (list_items pager / bulk_upsert), or the result
  // itself when it IS a board (create_board / get_board top-level shape).
  const board = result?.board ?? (looksLikeBoard(result) ? result : undefined);
  const rawBoardId = parseUrlParam(board?.web_url, 'boardID');
  if (!board || !rawBoardId) return null;

  // list_items carries `items` (or the v1 pager's `data`); bulk_upsert carries
  // `created` + `updated`; create_board carries no items.
  const rawItems: any[] = Array.isArray(result.items)
    ? result.items
    : Array.isArray(result.data)
      ? result.data
      : [...(Array.isArray(result.created) ? result.created : []),
         ...(Array.isArray(result.updated) ? result.updated : [])];

  const items = rawItems
    .map(adaptItem)
    .filter((it): it is BoardTaskItem => it !== null);

  return {
    board: {
      name: typeof board.name === 'string' && board.name ? board.name : 'Board',
      webUrl: board.web_url,
      rawBoardId,
    },
    items,
    action,
  };
}
